import React, { useState } from 'react'
import { CheckCircleFilled } from '@ant-design/icons'
import { Button } from 'antd'
import styled from 'styled-components'
import UpdateAddressBtn from './update-address-btn/update-address-btn'
import UpdateNationalAddressModal from './update-address-btn/update-national-address-modal'
import theme from '../../theme'
import { useTracking } from '../../hooks/useTracking'
import DetailCard from '../common/detail-card'
import { isOriginSaudi } from '../../utils/country'
import { OrderState } from '../../types/Order'

// The national address stays editable while the order is still pre-collection.
// The cut-off is ready_for_collection: from that point the parcel has been
// prepared for collection, so changing the destination address is too late.
// Everything at or past that point is therefore absent from this list.
// (sc-26828, boundary corrected in sc-29010)
const NATIONAL_ADDRESS_EDITABLE_STATES: OrderState[] = [
  'scheduled',
  'processing',
  'picking_started',
  'pending',
]

const VerifiedIcon = styled(CheckCircleFilled)`
  color: #00a854;
  margin-right: 4px;
`
const VerifiedMessage = styled.div`
  color: ${theme.slate500};
  font-size: 10px;
  line-height: 21px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 10px;
  font-weight: 100;
  font-size: 16px;
  line-height: 21px;
  color: ${theme.midnight800};
`

const NationalAddress = styled.div`
  font-size: 14px;
  color: ${theme.slate500};
  margin-top: 8px;
  display: flex;
`
const NationalAddressLabel = styled.div`
  font-weight: bold;
  margin-right: 4px;
`

const InvalidNationalAddressWarning = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 8px;
  line-height: 18px;
`

const UpdateNationalAddressButton = styled(Button)`
  margin-top: 8px;
`

type Props = {}

function DestinationInfo(_props: Props, ref: React.Ref<HTMLDivElement>) {
  const { orderDetails, hasRobotLeg } = useTracking()
  const [isNationalAddressModalOpen, setIsNationalAddressModalOpen] =
    useState(false)

  if (
    !orderDetails ||
    !orderDetails.destination ||
    !orderDetails.uuid ||
    orderDetails?.serviceKind === 'partner_return'
  ) {
    return null
  }

  const isSaudi = isOriginSaudi(orderDetails.origin?.address?.country)
  const metadata = orderDetails.destination.address.metadata
  const isNationalAddressValid = metadata?.isNationalAddressValid ?? true

  const isNationalAddressInvalid = isSaudi && isNationalAddressValid === false
  const hideAddressButton = orderDetails.stateChanges.some(
    (stateChange) => stateChange.toState === 'delivery_complete'
  )
  const hideNationalAddressButton = !NATIONAL_ADDRESS_EDITABLE_STATES.includes(
    orderDetails.state
  )

  return (
    <DetailCard ref={ref} title="📍 Delivery address">
      <Content>
        <div>
          {orderDetails.destination.contactName} -{' '}
          {orderDetails.destination.address.address1}
        </div>
        {isSaudi && (
          <NationalAddress>
            <NationalAddressLabel>National Address:</NationalAddressLabel>
            {orderDetails.destination.address.ksaNationalAddress || 'N/A'}
          </NationalAddress>
        )}
        {isSaudi ? (
          <>
            <UpdateNationalAddressModal
              isOpen={isNationalAddressModalOpen}
              onClose={() => setIsNationalAddressModalOpen(false)}
              orderUuid={orderDetails.uuid}
              destination={orderDetails.destination}
              currentNationalAddress={
                orderDetails.destination.address.ksaNationalAddress
              }
            />
            {isNationalAddressInvalid && (
              <InvalidNationalAddressWarning>
                All Saudi locations are required to have a valid Saudi national
                address to initiate the delivery
              </InvalidNationalAddressWarning>
            )}
            {!hideNationalAddressButton && (
              <UpdateNationalAddressButton
                onClick={() => setIsNationalAddressModalOpen(true)}
                type="default"
                block
                data-testid="update-national-address-btn"
              >
                Update National Address
              </UpdateNationalAddressButton>
            )}
          </>
        ) : (
          <UpdateAddressBtn
            address={orderDetails.destination}
            orderUuid={orderDetails.uuid}
            state={orderDetails.state}
            serviceKind={orderDetails.serviceKind}
            orderId={orderDetails.id}
            addressType="destination"
            // Once the parcel is with the robot the destination is fixed to the
            // building; the button would only ever show "unavailable" (sc-29236).
            hide={hideAddressButton || hasRobotLeg}
          />
        )}
        {orderDetails.destination.checked ? (
          <VerifiedMessage>
            <VerifiedIcon />
            Address confirmed
          </VerifiedMessage>
        ) : null}
      </Content>
    </DetailCard>
  )
}

export default React.forwardRef(DestinationInfo)
